import {
  CommitSourceArgs,
  CreateMultiDocumentUploadUrlArgs,
  CreateUploadUrlArgs,
  CreateUploadUrlResponse,
  InternalStakeholdersResponse,
  MAX_UPLOAD_BYTES,
  MAX_UPLOAD_BYTES_TEXT,
  OnboardingSectionResponse,
  OnboardingSectionVersion,
  OnboardingStoreType,
  OracleOnboardingSectionKey,
  PDF_CONTENT_TYPE,
  RetrySummaryArgs,
  SectionArgs,
  SourceDownloadUrlArgs,
  SourceDownloadUrlResponse
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { awaitJSON, OracleLambdaFetch, fetchPresigned } from '@src/utils/fetch-utils';

export const fetchSection = async ({
  company,
  store,
  sectionKey
}: SectionArgs): Promise<OnboardingSectionResponse> => {
  const res = await OracleLambdaFetch('/onboarding/section', {
    params: { company, store, sectionKey }
  });
  return awaitJSON<OnboardingSectionResponse>(res);
};

export const fetchInternalStakeholders = async (
  company: string
): Promise<InternalStakeholdersResponse> => {
  const res = await OracleLambdaFetch('/onboarding/stakeholders', { params: { company } });
  return awaitJSON<InternalStakeholdersResponse>(res);
};

// Returns the message to show, or null when the file is acceptable. Shared with the modal so
// the checks and their copy cannot drift apart.
export const getSourceFileValidationError = (file: File): string | null => {
  if (file.type !== PDF_CONTENT_TYPE) {
    return 'Only PDF files are supported.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `Documents must be ${MAX_UPLOAD_BYTES_TEXT} or smaller.`;
  }
  if (file.size === 0) {
    return 'This file is empty.';
  }
  return null;
};

interface UploadArgs {
  company: string;
  // Which store takes the bytes, and which versions table records them.
  store: OnboardingStoreType;
  sectionKey: OracleOnboardingSectionKey;
  file: File;
  editorName: string;
}

// A store that names required headers wants exactly those replayed; an empty map still needs
// Content-Type, or the PUT is rejected.
export const putToUploadUrl = async (
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  file: File
): Promise<void> => {
  const headers =
    Object.keys(requiredHeaders).length > 0 ? requiredHeaders : { 'Content-Type': file.type };
  await fetchPresigned(uploadUrl, 'Upload failed', { method: 'PUT', body: file, headers });
};

// Mints a presigned URL at `path`, then PUTs the bytes to it. Returns the minted file id.
export const uploadToPresignedUrl = async (
  path: string,
  body: CreateUploadUrlArgs | CreateMultiDocumentUploadUrlArgs,
  file: File
): Promise<string> => {
  const validationError = getSourceFileValidationError(file);
  if (validationError) {
    throw new Error(validationError);
  }
  const res = await OracleLambdaFetch(path, { method: 'post', body });
  const { uploadUrl, fileId, requiredHeaders } = await awaitJSON<CreateUploadUrlResponse>(res);
  await putToUploadUrl(uploadUrl, requiredHeaders, file);
  return fileId;
};

// Uploads the bytes, then commits them as the section's new version.
export const uploadSourceDocument = async ({
  company,
  store,
  sectionKey,
  file,
  editorName
}: UploadArgs): Promise<OnboardingSectionVersion> => {
  // The file record is minted before any bytes move, so the name and size go up front.
  const fileId = await uploadToPresignedUrl(
    '/onboarding/source/upload-url',
    {
      company,
      store,
      sectionKey,
      contentType: file.type,
      fileName: file.name,
      sizeBytes: file.size
    },
    file
  );

  const commitBody: CommitSourceArgs = {
    company,
    store,
    sectionKey,
    fileId,
    fileName: file.name,
    editorName
  };
  const commitRes = await OracleLambdaFetch('/onboarding/source', {
    method: 'post',
    body: commitBody
  });
  return awaitJSON<OnboardingSectionVersion>(commitRes);
};

// Signs the key server-side rather than exposing bucket/key to the caller. Call this on click,
// not on render: a URL minted at render time may be expired by the time anyone clicks it.
export const fetchSourceDownloadUrl = async ({
  company,
  store,
  versionId
}: SourceDownloadUrlArgs): Promise<string> => {
  const res = await OracleLambdaFetch('/onboarding/source/download', {
    params: { company, store, versionId: String(versionId) }
  });
  const { url } = await awaitJSON<SourceDownloadUrlResponse>(res);
  return url;
};

export const SourceOpenResult = {
  OPENED: 'opened',
  CANCELLED: 'cancelled',
  POPUP_BLOCKED: 'popup-blocked'
} as const;
export type SourceOpenResultType = (typeof SourceOpenResult)[keyof typeof SourceOpenResult];

// The tab is opened before the await, while the click still carries user activation --
// afterwards the browser treats it as script-initiated and blocks it. 'noopener' cannot be
// passed to window.open here: it makes the call return null, so the opener is severed by hand.
// Throws if the URL cannot be minted; the caller decides how to surface that.
export const openDocumentInNewTab = async (
  fetchUrl: () => Promise<string>
): Promise<SourceOpenResultType> => {
  const tab = window.open('', '_blank');
  if (!tab) {
    return SourceOpenResult.POPUP_BLOCKED;
  }
  tab.opener = null;
  try {
    const url = await fetchUrl();
    // Closing the blank tab during the fetch is how a user cancels this.
    if (tab.closed) {
      return SourceOpenResult.CANCELLED;
    }
    tab.location.href = url;
    return SourceOpenResult.OPENED;
  } catch (error) {
    tab.close();
    throw error;
  }
};

// Re-runs the summary against a version's existing document. Nothing is re-uploaded: this is for
// the case where the document landed and only the summarization failed.
export const retrySummary = async (
  company: string,
  store: OnboardingStoreType,
  versionId: number,
  editorName: string
): Promise<OnboardingSectionVersion> => {
  const body: RetrySummaryArgs = { company, store, versionId, editorName };
  const res = await OracleLambdaFetch('/onboarding/section/retry', { method: 'post', body });
  return awaitJSON<OnboardingSectionVersion>(res);
};
